import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, User, Phone, CreditCard, ShieldCheck, ChevronDown, ChevronRight, ExternalLink, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const CreditHistorySearch = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchBy, setSearchBy] = useState('name');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [expandedEntrepreneur, setExpandedEntrepreneur] = useState(null);

  const searchByOptions = [
    { value: 'name', label: 'Name', icon: User, placeholder: 'Enter member name...' },
    { value: 'mobile', label: 'Mobile No', icon: Phone, placeholder: 'Enter mobile number...' },
    { value: 'pan', label: 'PAN Card', icon: CreditCard, placeholder: 'Enter PAN number (e.g. ABCDE1234F)...' },
    { value: 'aadhaar', label: 'Aadhaar No', icon: ShieldCheck, placeholder: 'Enter Aadhaar number...' },
  ];

  const handleSearch = async () => {
    if (!searchQuery || searchQuery.trim().length < 2) {
      toast.error('Please enter at least 2 characters to search');
      return;
    }
    setLoading(true);
    setSearched(true);
    try {
      const resp = await api.get('/reports/credit-history-search', {
        params: { q: searchQuery.trim(), search_by: searchBy }
      });
      setResults(resp.data.results || []);
      if ((resp.data.results || []).length === 0) {
        toast.info('No members found matching your search');
      }
    } catch (error) {
      toast.error('Failed to search credit history');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const getScoreBadge = (score) => {
    if (score === 'Good') return 'bg-emerald-100 text-emerald-800 border-emerald-300';
    if (score === 'Moderate') return 'bg-amber-100 text-amber-800 border-amber-300';
    return 'bg-red-100 text-red-800 border-red-300';
  };

  const getStatusBadge = (status) => {
    if (status === 'On Track') return 'bg-emerald-50 text-emerald-700';
    if (status === 'Moderate Delay') return 'bg-amber-50 text-amber-700';
    return 'bg-red-50 text-red-700';
  };

  const currentOption = searchByOptions.find(o => o.value === searchBy);

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="credit-history-search-page">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <Link to="/reports" className="text-slate-500 hover:text-slate-700">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Credit History Search</h1>
        </div>
        <p className="text-sm text-slate-600 ml-8">Search individual credit history by Name, Mobile, PAN or Aadhaar</p>
      </div>

      {/* Search Section */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-6" data-testid="search-section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
          <div className="md:col-span-3 space-y-2">
            <Label className="text-xs font-medium text-slate-600">Search By</Label>
            <Select value={searchBy} onValueChange={(v) => { setSearchBy(v); setSearchQuery(''); }}>
              <SelectTrigger data-testid="search-by-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {searchByOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-7 space-y-2">
            <Label className="text-xs font-medium text-slate-600">Search Query</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={currentOption?.placeholder}
                className="pl-10"
                data-testid="search-query-input"
              />
            </div>
          </div>
          <div className="md:col-span-2">
            <Button
              onClick={handleSearch}
              disabled={loading}
              className="w-full bg-emerald-700 hover:bg-emerald-800 text-white"
              data-testid="search-button"
            >
              {loading ? 'Searching...' : 'Search'}
            </Button>
          </div>
        </div>
      </div>

      {/* Results */}
      {searched && !loading && results.length === 0 && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-12 text-center">
          <Search className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p className="text-slate-500">No members found matching your search criteria</p>
        </div>
      )}

      {results.length > 0 && (
        <div className="space-y-4">
          <p className="text-sm text-slate-600 font-medium">{results.length} member(s) found</p>

          {results.map((member) => (
            <div
              key={member.entrepreneur_id}
              className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"
              data-testid={`member-card-${member.entrepreneur_id}`}
            >
              {/* Member Header */}
              <div
                className="p-4 md:p-5 cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => setExpandedEntrepreneur(
                  expandedEntrepreneur === member.entrepreneur_id ? null : member.entrepreneur_id
                )}
                data-testid={`member-toggle-${member.entrepreneur_id}`}
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-slate-500" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-base font-semibold text-slate-900">{member.name}</h3>
                        <Link
                          to={`/entrepreneurs/${member.entrepreneur_id}`}
                          className="text-blue-600 hover:text-blue-800"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`member-profile-link-${member.entrepreneur_id}`}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Link>
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 mt-0.5">
                        {member.mobile && <span>Mobile: {member.mobile}</span>}
                        {member.pan_number && <span>PAN: {member.pan_number}</span>}
                        {member.aadhaar_number && <span>Aadhaar: {member.aadhaar_number}</span>}
                        <span>{member.branch_name} ({member.branch_code})</span>
                        {member.shg_vlf_name && <span>SHG: {member.shg_vlf_name}</span>}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div>
                        <p className="text-xs text-slate-500">Loans</p>
                        <p className="text-sm font-bold text-slate-900">{member.total_loans}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Outstanding</p>
                        <p className="text-sm font-bold text-red-700">Rs.{member.total_outstanding.toLocaleString('en-IN')}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Repayment</p>
                        <p className="text-sm font-bold text-slate-900">{member.repayment_percentage}%</p>
                      </div>
                    </div>
                    <span className={`text-xs px-2.5 py-1 rounded-full border font-semibold ${getScoreBadge(member.credit_score)}`}>
                      {member.credit_score}
                    </span>
                    {expandedEntrepreneur === member.entrepreneur_id ?
                      <ChevronDown className="w-5 h-5 text-slate-400" /> :
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    }
                  </div>
                </div>
              </div>

              {/* Expanded Loan Details */}
              {expandedEntrepreneur === member.entrepreneur_id && (
                <div className="border-t border-slate-200 bg-slate-50 p-4 md:p-5">
                  {member.loans.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">No loan records found</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid={`loans-table-${member.entrepreneur_id}`}>
                        <thead>
                          <tr className="bg-slate-200 text-slate-700">
                            <th className="px-3 py-2 text-left font-semibold">Loan ID</th>
                            <th className="px-3 py-2 text-left font-semibold">Type</th>
                            <th className="px-3 py-2 text-right font-semibold">Amount</th>
                            <th className="px-3 py-2 text-left font-semibold">Disbursed</th>
                            <th className="px-3 py-2 text-right font-semibold">EMI</th>
                            <th className="px-3 py-2 text-right font-semibold">Collected</th>
                            <th className="px-3 py-2 text-right font-semibold">Outstanding</th>
                            <th className="px-3 py-2 text-center font-semibold">Missed</th>
                            <th className="px-3 py-2 text-center font-semibold">Repay%</th>
                            <th className="px-3 py-2 text-center font-semibold">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 bg-white">
                          {member.loans.map((loan) => (
                            <tr key={loan.loan_id} className="hover:bg-blue-50 transition-colors">
                              <td className="px-3 py-2">
                                <Link
                                  to={`/loans/${loan.loan_uuid}`}
                                  className="text-blue-700 hover:text-blue-900 font-medium hover:underline"
                                  data-testid={`loan-link-${loan.loan_id}`}
                                >
                                  {loan.loan_id}
                                </Link>
                              </td>
                              <td className="px-3 py-2">
                                <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-800">{loan.loan_type}</span>
                              </td>
                              <td className="px-3 py-2 text-right font-medium">Rs.{loan.loan_amount.toLocaleString('en-IN')}</td>
                              <td className="px-3 py-2 text-slate-600">
                                {loan.disbursement_date ? new Date(loan.disbursement_date).toLocaleDateString('en-IN') : '-'}
                              </td>
                              <td className="px-3 py-2 text-right">Rs.{loan.emi_amount.toLocaleString('en-IN')}</td>
                              <td className="px-3 py-2 text-right text-emerald-700 font-medium">Rs.{loan.total_collected.toLocaleString('en-IN')}</td>
                              <td className="px-3 py-2 text-right text-red-700 font-medium">Rs.{loan.outstanding.toLocaleString('en-IN')}</td>
                              <td className="px-3 py-2 text-center">
                                <span className={`font-bold ${loan.missed_emis > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                                  {loan.missed_emis}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-center font-medium">{loan.repayment_percentage}%</td>
                              <td className="px-3 py-2 text-center">
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getStatusBadge(loan.repayment_status)}`}>
                                  {loan.repayment_status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="flex justify-end mt-3">
                    <Link
                      to={`/entrepreneurs/${member.entrepreneur_id}`}
                      className="text-sm text-blue-700 hover:text-blue-900 font-medium flex items-center gap-1"
                      data-testid={`view-profile-${member.entrepreneur_id}`}
                    >
                      View Member Profile <ExternalLink className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CreditHistorySearch;
